import { useEffect } from 'react';

interface Seo {
    title: string;
    description: string;
    // Ruta absoluta desde public/, empezando con barra. Se convierte a URL absoluta.
    // (Sin ejemplo literal acá: el deploy busca rutas /images/... en src/ para chequear que
    // el archivo exista, y una ruta de ejemplo en un comentario le da un falso positivo.)
    image?: string;
    /** Path de la página. Por defecto, el actual. */
    path?: string;
}

const SITIO = 'https://www.ironfive.ar';

const setMeta = (attr: 'name' | 'property', key: string, value: string) => {
    let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', value);
};

const setCanonical = (href: string) => {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
    }
    link.setAttribute('href', href);
};

/**
 * Actualiza title y meta tags al navegar.
 *
 * OJO — esto NO alcanza para el preview de los links. WhatsApp, LinkedIn y Slack leen el
 * HTML crudo y no ejecutan JavaScript: para ellos el `<meta og:*>` es el que vino en el
 * index.html, no el que este hook escribe después. Lo que hace que el preview salga bien
 * es `scripts/generate-og-pages.js`, que en el build genera un index.html por proyecto con
 * los tags ya puestos.
 *
 * Este hook sirve igual para: el título de la pestaña, el nombre del favorito al guardar
 * la página, y los crawlers que sí ejecutan JS (Google entre ellos).
 */
export const useSeo = ({ title, description, image, path }: Seo) => {
    useEffect(() => {
        const url = SITIO + (path ?? window.location.pathname);
        const imagenAbs = image ? (image.startsWith('http') ? image : SITIO + image) : undefined;

        document.title = title;
        setMeta('name', 'description', description);
        setCanonical(url);

        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:url', url);
        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:site_name', 'Iron Five');

        setMeta('name', 'twitter:card', imagenAbs ? 'summary_large_image' : 'summary');
        setMeta('name', 'twitter:title', title);
        setMeta('name', 'twitter:description', description);

        if (imagenAbs) {
            setMeta('property', 'og:image', imagenAbs);
            setMeta('name', 'twitter:image', imagenAbs);
        }
    }, [title, description, image, path]);
};
